import React, { startTransition, useEffect, useRef } from 'react'
import { useAuth } from '../auth/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { useSettingsStore } from '../stores/settingsStore'
import { useMarketStore } from '../services/store'
import { getFirebaseAuth } from '../services/firebase'
import { applyChainDiff } from '../lib/diff-engine'
import ky from 'ky'
import type { MarketAnalysisResponse } from '@options-oracle/types'
import { writeLiveTick } from '../stores/liveTickAtoms'
import { toast } from 'sonner'
import { useAlertStore } from '../hooks/useAlertStore'

let globalLastStoreWrite = 0

/**
 * GlobalMarketEngine
 * 
 * A headless "Daemon" component mounted exactly ONCE in the application root (_auth.tsx).
 * This component takes over all network polling and data fetching. It pushes the fetched data 
 * into the global Zustand store.
 * 
 * WHY: Previously, useDashboardData handled both fetching and selecting. Because useDashboardData
 * is used in 9+ different components, it spawned 9 concurrent fetchers/subscribers. Every 2 seconds,
 * all 9 components would re-render simultaneously, freezing the UI.
 * By centralizing the fetch here, the UI components only re-render when Zustand notifies them of 
 * a STRUCTURAL data change.
 */
export function GlobalMarketEngine() {
    // 1. Subscribe ONLY to the necessary settings
    const activeSymbol = useSettingsStore( s => s.symbol ) || ''
    const settingsExpiry = useSettingsStore( s => s.expiry )
    const activeMode = useSettingsStore( s => s.mode )
    const settingsRange = useSettingsStore( s => s.range )
    const settingsBaseTime = useSettingsStore( s => s.baseTime )
    const settingsIsLive = useSettingsStore( s => s.isLive )
    const settingsHistoricalDate = useSettingsStore( s => s.historicalDate )
    const inspectionSource = useSettingsStore( s => s.inspectionSource )

    const symbolExpiries = useMarketStore( ( state ) => state.expiryMap[ activeSymbol ] ) || []
    const effectiveExpiry: string = settingsExpiry || ( symbolExpiries.length > 0 ? ( symbolExpiries[ 0 ] as string ) : '' )
    const spotPrice = useMarketStore( ( state ) => state.indexLtp[ activeSymbol ] || 0 )

    const { isAuthenticated, loading, firebaseUser } = useAuth()
    
    // ========================================================================
    // EFFICIENT EXPIRY FETCHING (Direct API, no tRPC)
    // ========================================================================
    const { data: fetchedExpiries } = useQuery( {
        queryKey: [ 'expiries', activeSymbol, settingsIsLive ? 'live' : settingsHistoricalDate ],
        queryFn: async () => {
            if ( !activeSymbol ) return []
            let url = `/api/expiries?symbol=${encodeURIComponent( activeSymbol )}`
            if ( !settingsIsLive && settingsHistoricalDate ) {
                url += `&date=${encodeURIComponent( settingsHistoricalDate )}`
            }
            const resp = await fetch( url )
            if ( !resp.ok ) return []
            return resp.json() as Promise<string[]>
        },
        enabled: !!activeSymbol && inspectionSource !== 'HistoricalSnapshot',
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    } )

    // Sync expiries with Zustand store (ONLY for Live mode; Historical Mode gets expiries from Parquet / Historical feed)
    useEffect( () => {
        if ( inspectionSource === 'HistoricalSnapshot' ) return;
        if ( fetchedExpiries && Array.isArray( fetchedExpiries ) && fetchedExpiries.length > 0 ) {
            const store = useMarketStore.getState()
            const currentExpiries = store.expiryMap[ activeSymbol ] || []
            if ( JSON.stringify( currentExpiries ) !== JSON.stringify( fetchedExpiries ) ) {
                console.log(
                    `[GlobalMarketEngine] Syncing ${fetchedExpiries.length} expiry dates from getExpiries endpoint for ${activeSymbol}`,
                    fetchedExpiries,
                )
                store.setExpiryForSymbol( activeSymbol, fetchedExpiries as string[] )
            }

            // CRITICAL: When switching back to Live mode from Historical mode, settings.expiry might still hold an old historical date that is invalid in Live mode!
            const currentSelectedExpiry = useSettingsStore.getState().expiry;
            if ( !currentSelectedExpiry || !fetchedExpiries.includes( currentSelectedExpiry ) ) {
                const firstLiveExpiry = fetchedExpiries[ 0 ];
                console.log(`[GlobalMarketEngine] Selected expiry "${currentSelectedExpiry}" is invalid for Live mode. Auto-switching to Live expiry: ${firstLiveExpiry}`);
                setTimeout(() => useSettingsStore.getState().setExpiry( firstLiveExpiry! ), 0);
            }
        }
    }, [ fetchedExpiries, activeSymbol, inspectionSource ] )

    // ========================================================================
    // HIGH-PERFORMANCE DIRECT LIVE DATA FETCH (Bypassing tRPC)
    // ========================================================================
    const { data: workerAnalysis } = useQuery( {
        queryKey: [ 'live-market-data', activeSymbol, activeMode, settingsRange, settingsBaseTime, effectiveExpiry, !!firebaseUser ],
        queryFn: async () => {
            if ( !activeSymbol || !effectiveExpiry ) return null

            // Use the direct Cloudflare Pages proxy route for maximum speed, bypassing tRPC batch overhead
            const url = new URL( '/api/worker-proxy', window.location.origin )
            url.searchParams.set( 'symbol', activeSymbol )
            url.searchParams.set( 'mode', activeMode )
            url.searchParams.set( 'expiry', effectiveExpiry )
            url.searchParams.set( 'range', settingsRange.toString() )
            if ( settingsBaseTime ) url.searchParams.set( 'baseTime', settingsBaseTime )
            url.searchParams.set( 't', Date.now().toString() ) // Cache buster

            const token = (await firebaseUser?.getIdToken()) || (await getFirebaseAuth().currentUser?.getIdToken())
            if (!token) return null
            const res = await fetch( url.toString(), { 
                cache: 'no-store',
                headers: { Authorization: `Bearer ${token}` }
            } )
            if ( !res.ok ) throw new Error( 'Failed to fetch live data' )
            return res.json() as Promise<MarketAnalysisResponse>
        },
        enabled: !!activeSymbol && !!effectiveExpiry && inspectionSource !== 'HistoricalSnapshot' && isAuthenticated && !loading && !!firebaseUser,
        refetchInterval: (query) => 1000, // Poll every 1 second for near-realtime
        refetchIntervalInBackground: true, // CRITICAL: Keep polling even if window loses focus (e.g., secondary monitor)
        staleTime: 500, // Data is stale after 0.5s
        refetchOnWindowFocus: true, // Resume polling on focus
        refetchOnMount: true,
        retry: 2,
    } )

    // ========================================================================
    // HYDRATE ZUSTAND STORE
    // ========================================================================
    // FREEZE FIX: Memoize chainRows to prevent creating new array references
    // when the underlying options data hasn't changed.
    const prevChainRef = useRef<{ options: any[]; rows: any[] } | null>(null)
    const prevMetaRef = useRef<any>(null)

    useEffect( () => {
        if ( !workerAnalysis || !activeSymbol ) return

        const waSpot = workerAnalysis.spotPrice || ( workerAnalysis as any ).ltp

        if ( !waSpot || waSpot <= 0 ) {
            if ( inspectionSource !== 'HistoricalSnapshot' ) {
                console.warn( `[GlobalMarketEngine] spotPrice is ${waSpot} for ${activeSymbol}. Skipping store update.` )
            }
            return
        }

        // Throttle store updates to prevent cascade loops
        const now = Date.now()
        const timeSinceLastWrite = now - globalLastStoreWrite
        if ( timeSinceLastWrite < 100 ) return
        globalLastStoreWrite = now

        const waVix = ( workerAnalysis as any ).vix || 0
        const waDayChange = ( workerAnalysis as any ).dayChange || ( workerAnalysis as any ).ltpChange || 0
        const waDayChangeP = ( workerAnalysis as any ).dayChangeP || ( workerAnalysis as any ).ltpChangePercent || 0
        const waVixChange = ( workerAnalysis as any ).vixChange || 0
        const waVixChangeP = ( workerAnalysis as any ).vixChangeP || 0
        const waVixChangePercent = ( workerAnalysis as any ).vixChangePercent || ( workerAnalysis as any ).vixChangeP || 0
        const waMetrics = workerAnalysis.analysis

        // FREEZE FIX: NO timestamp in meta.
        // Previously `timestamp: Date.now()` was included, creating a NEW meta object
        // reference every 2s poll. Zustand notified ALL subscribers, triggering
        // 8 useDashboardData instances to recompute 130-option analysis simultaneously.
        // Timestamp is now read non-reactively via Date.now() where needed for display.
        const meta = {
            dayChange: waDayChange,
            dayChangeP: waDayChangeP,
            vix: waVix,
            vixChange: waVixChange,
            vixChangeP: waVixChangeP,
            vixChangePercent: waVixChangePercent,
        }

        // FREEZE FIX: Skip updateTick if meta values haven't changed.
        // This prevents Zustand from creating new object references when
        // the actual display values are identical between polls.
        const prevMeta = prevMetaRef.current
        const metaUnchanged = prevMeta &&
            prevMeta.dayChange === meta.dayChange &&
            prevMeta.dayChangeP === meta.dayChangeP &&
            prevMeta.vix === meta.vix &&
            prevMeta.vixChange === meta.vixChange

        const waOptions = workerAnalysis.options || ( workerAnalysis as any ).optionChain || []

        // FREEZE FIX: Reuse previous chainRows reference if data hasn't changed.
        // Without this, every 2s poll creates 130 new objects via .map(),
        // producing a new array reference that triggers Zustand notifications
        // and cascading re-renders across all subscribed components.
        let chainRows: any[]
        const prevChain = prevChainRef.current
        
        const rawMappedChain = waOptions
            .map( ( opt: any ) => ( {
                strike: opt.strike,
                ce: {
                    ltp: opt.callPrice || 0,
                    oi: opt.callOpenInterest || 0,
                    vol: opt.callVolume || 0,
                    oiChange: opt.callOpenInterestChange || 0,
                    change: opt.callChange || 0,
                    changePercent: opt.callChangePercent || 0,
                    iv: opt.callImpliedVolatility || 0,
                    delta: opt.callDelta || 0,
                    gamma: opt.callGamma || 0,
                    theta: opt.callTheta || 0,
                    vega: opt.callVega || 0,
                    instrumentKey:
                        opt.ceInstrumentKey || opt.callInstrumentKey || opt.ce?.instrumentKey || opt.instrumentKey,
                },
                pe: {
                    ltp: opt.putPrice || 0,
                    oi: opt.putOpenInterest || 0,
                    vol: opt.putVolume || 0,
                    oiChange: opt.putOpenInterestChange || 0,
                    change: opt.putChange || 0,
                    changePercent: opt.putChangePercent || 0,
                    iv: opt.putImpliedVolatility || 0,
                    delta: opt.putDelta || 0,
                    gamma: opt.putGamma || 0,
                    theta: opt.putTheta || 0,
                    vega: opt.putVega || 0,
                    instrumentKey:
                        opt.peInstrumentKey || opt.putInstrumentKey || opt.pe?.instrumentKey || opt.instrumentKey,
                },
            } ) )
            .sort( ( a: any, b: any ) => a.strike - b.strike )
            
        // Use Diff Engine to preserve referential equality for unchanged strikes
        chainRows = applyChainDiff(prevChain?.rows, rawMappedChain)
        
        if (chainRows !== prevChain?.rows) {
            prevChainRef.current = { options: waOptions, rows: chainRows }
        }

        // FREEZE FIX: If chain, LTP, and meta are all unchanged, skip the heavy store update.
        // However, we MUST still heartbeat the Jotai atoms and the global timestamp so 
        // the header (LiveTickerDisplay) updates every 2 seconds to show connection is alive.
        const store = useMarketStore.getState()
        const prevLtp = store.indexLtp[activeSymbol]

        // 🚀 PHASE 2: Dual-write to Jotai atoms for surgical component re-renders.
        const fullMarketData = {
            symbol: activeSymbol,
            spotPrice: waSpot,
            dayChange: meta.dayChange,
            dayChangeP: meta.dayChangeP,
            vix: meta.vix,
            vixChange: meta.vixChange,
            vixChangeP: meta.vixChangeP,
            options: waOptions,
            timestamp: Date.now(),
            expiry: effectiveExpiry,
            isLive: true,
            atmStrike: waMetrics?.atmStrike || 0,
        } as any
        
        writeLiveTick(activeSymbol, waSpot, meta, waMetrics, fullMarketData)

        if (prevChain?.rows === chainRows && prevLtp === waSpot && metaUnchanged) {
            // Nothing changed structurally — heartbeat timestamp only
            store.updateTimestamp()
            return
        }
        prevMetaRef.current = meta

        // PERF: startTransition dropped because it delays state updates and misses ticks in high-freq streaming
        store.updateTick( activeSymbol, waSpot, chainRows, meta, waMetrics )

        // ========================================================================
        // EVALUATE CUSTOM ALERTS
        // ========================================================================
        try {
            const { alerts, updateLastTriggered, toggleAlert } = useAlertStore.getState()
            const activeAlerts = alerts.filter((a: any) => a.isActive && a.symbol === activeSymbol)
            
            if (activeAlerts.length > 0) {
                const now = Date.now()
                for (const alert of activeAlerts) {
                    // Don't trigger same alert more than once per minute
                    if (alert.lastTriggered && now - alert.lastTriggered < 60000) continue;

                    const row = chainRows.find(r => r.strike === alert.strike)
                    if (!row) continue;

                    const data = alert.type === 'CE' ? row.ce : row.pe
                    if (!data) continue;

                    let currentValue = 0
                    if (alert.metric === 'ltp') currentValue = data.ltp
                    else if (alert.metric === 'oi') currentValue = data.oi
                    else if (alert.metric === 'oiChg') currentValue = data.oiChange
                    else if (alert.metric === 'volume') currentValue = data.vol
                    else if (alert.metric === 'volOiDiff') currentValue = data.vol - data.oi
                    else if (alert.metric === 'volOiChg') currentValue = data.vol - data.oiChange // Approximation for live data

                    let isTriggered = false
                    if (alert.operator === 'gt' && currentValue > alert.threshold) isTriggered = true
                    if (alert.operator === 'lt' && currentValue < alert.threshold) isTriggered = true

                    if (isTriggered) {
                        toast.success(`Alert Triggered: ${alert.symbol} ${alert.strike} ${alert.type}`, {
                            description: `${alert.metric.toUpperCase()} is ${alert.operator === 'gt' ? '>' : '<'} ${alert.threshold.toLocaleString('en-IN')} (Current: ${currentValue.toLocaleString('en-IN')})`,
                            duration: 8000,
                        })
                        updateLastTriggered(alert.id, now)
                        
                        // Play a sound if you want to implement it later
                        try {
                            const audio = new Audio('/alert-chime.mp3')
                            audio.play().catch(err => console.error("[AsyncCatch] Operation failed:", err))
                        } catch (e) { console.warn('Ignored error in GlobalMarketEngine.tsx:', e); }
                    }
                }
            }
        } catch (err) {
            console.error('[GlobalMarketEngine] Error evaluating alerts:', err)
        }

    }, [ workerAnalysis, activeSymbol, effectiveExpiry, inspectionSource ] )

    // ========================================================================
    // BACKUP LOGIC (Once per minute)
    // ========================================================================
    const lastBackupTime = useRef<number>( 0 )
    useEffect( () => {
        const backupOptions = workerAnalysis?.options || ( workerAnalysis as any )?.optionChain || []
        if ( !workerAnalysis || backupOptions.length === 0 ) return

        const now = Date.now()
        // Throttle backup to once every ~60 seconds
        if ( now - lastBackupTime.current > 60000 ) {
            lastBackupTime.current = now

            const auth = getFirebaseAuth()
            const user = auth.currentUser
            if ( !user ) return

            user.getIdToken().then( ( token ) => {
                const snapshot = {
                    symbol: activeSymbol,
                    expiry: effectiveExpiry,
                    timestamp: workerAnalysis.timestamp || now,
                    spotPrice: spotPrice || ( workerAnalysis as any ).ltp || workerAnalysis.spotPrice,
                    pcr: workerAnalysis.analysis?.pcr || 0,
                    options: backupOptions,
                    analysis: workerAnalysis.analysis,
                }

                if ( window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ) return

                ky.post( '/api/backup', {
                    json: {
                        symbol: activeSymbol,
                        expiry: effectiveExpiry,
                        snapshot: snapshot,
                    },
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 20000,
                    retry: 0,
                } ).catch( () => {} )
            } )
        }
    }, [ workerAnalysis, activeSymbol, effectiveExpiry, spotPrice ] )

    return null
}
